import React, { useEffect, useRef, useState } from 'react';
import { useDisclosure, useToast, Progress, Box, HStack, RadioGroup, Radio } from '@chakra-ui/react';
import { FaRegTrashAlt } from "react-icons/fa";
import { BiPlus } from "react-icons/bi";
import ImagePreviewModal from '../Modals/ImagePreviewModal';

const MultiImageField: React.FC<any> = ({ field, value = [], onFilesChange, invalid }) => {
  const [images, setImages] = useState<string[]>(Array.isArray(value) ? value : []);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const initialMode: 'na' | 'val' = value === 'N/A' ? 'na' : 'val';
  const [mode, setMode] = useState<'na' | 'val'>(initialMode);

  useEffect(() => {
    if (Array.isArray(value)) {
      setImages(value);
      setMode('val');
    } else if (value === 'N/A') {
      setMode('na');
      setImages([]);
    } else if (value) {
      setImages([value]);
      setMode('val');
    } else {
      setImages([]);
      setMode('val');
    }
  }, [value]);

  const handleFileChange = (file: File) => {
    if (mode === 'na') return;
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      setUploadingImage(URL.createObjectURL(file));
      setUploadProgress(0);
      reader.onprogress = (event) => {
        if (event.loaded && event.total) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      };
      reader.onloadend = () => {
        const result = reader.result as string;
        const newImages = [...images, result];
        setImages(newImages);
        onFilesChange?.(newImages);
        setUploadingImage(null);
        setUploadProgress(0);
      };
      reader.readAsDataURL(file);
    } else {
      toast({
        title: 'Not a valid image',
        description: 'Please upload a valid image file',
        status: 'warning',
        position: 'top',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleRemoveImage = (index: number) => {
    if (mode === 'na') return;
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    onFilesChange?.(updatedImages);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (mode === 'na') return;
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleAreaClick = () => {
    if (mode === 'na') return;
    fileInputRef.current?.click();
  };

  return (
    <div data-field-name={field?.name}>
      <b>{field.label}</b>
      {!field.nullable && <span style={{ color: "red" }}>*</span>}

      {field.nullable === true && (
        <RadioGroup value={mode} onChange={n => setMode(n as 'na' | 'val')}>
          <HStack mb={2}>
            <Radio value="na">TBD</Radio>
          </HStack>
          <HStack align="start" spacing={2}>
            <Radio value="val" aria-label="Provide values" mt="6px" />
            <div
              className="multi-image-field-container"
              style={{ flex: 1, opacity: mode === 'na' ? 0.6 : 1, pointerEvents: mode === 'na' ? 'none' : 'auto' }}
            >
              <div className="multi-img-preview">
                {images.map((image, index) => (
                  <div key={index} className="img-preview">
                    <div style={{ position: 'relative' }}>
                      <img
                        src={image}
                        alt={`Uploaded ${index}`}
                        onClick={() => { setPreviewImage(image); onOpen(); }}
                      />
                    </div>
                    <button type="button" className="remove-img-btn" onClick={() => handleRemoveImage(index)}>
                      <FaRegTrashAlt />
                    </button>
                  </div>
                ))}
                {uploadingImage && (
                  <Box style={{ position: 'relative', width: '100px', height: '100px', background: '#f0f0f0' }}>
                    <Progress value={uploadProgress} size="xs" position="absolute" bottom="0" width="100%" />
                  </Box>
                )}
              </div>

              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={handleAreaClick}
                className={!!invalid ? 'multi-img-upload-area invalid' : 'multi-img-upload-area'}
              >
                <BiPlus />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files) Array.from(files).forEach(handleFileChange);
                  }}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </HStack>
        </RadioGroup>
      )}

      {field.nullable !== true && (
        <div className="multi-image-field-container">
          <div className="multi-img-preview">
            {images.map((image, index) => (
              <div key={index} className="img-preview">
                <div style={{ position: 'relative' }}>
                  <img
                    src={image}
                    alt={`Uploaded ${index}`}
                    onClick={() => { setPreviewImage(image); onOpen(); }}
                  />
                </div>
                <button type="button" className="remove-img-btn" onClick={() => handleRemoveImage(index)}>
                  <FaRegTrashAlt />
                </button>
              </div>
            ))}
            {uploadingImage && (
              <Box style={{ position: 'relative', width: '100px', height: '100px', background: '#f0f0f0' }}>
                <Progress value={uploadProgress} size="xs" position="absolute" bottom="0" width="100%" />
              </Box>
            )}
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={handleAreaClick}
            className={!!invalid ? 'multi-img-upload-area invalid' : 'multi-img-upload-area'}
          >
            <BiPlus />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => {
                const files = e.target.files;
                if (files) Array.from(files).forEach(handleFileChange);
              }}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      )}

      {mode === 'na' ? (
        <input type="hidden" name={field.name} value="N/A" />
      ) : (
        <input
          type="hidden"
          name={field.name}
          value={images.length > 0 ? JSON.stringify(images) : ''}
        />
      )}

      {isOpen && previewImage && (
        <ImagePreviewModal isOpen={isOpen} onClose={onClose} image={previewImage} />
      )}
    </div>
  );
};

export default MultiImageField;
